import React, { useCallback, useEffect, useRef } from "react";

// Selector for elements that can receive keyboard focus inside the dialog.
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const getFocusable = (node) =>
  node
    ? Array.from(node.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      )
    : [];

/**
 * Accessible modal dialog primitive.
 *
 * - role="dialog" + aria-modal="true", with caller-supplied aria-labelledby
 *   (title) and aria-describedby (description) ids.
 * - Moves focus into the dialog on open (initialFocusRef, else first focusable,
 *   else the dialog itself) and restores it to the trigger on close.
 * - Traps Tab / Shift+Tab within the dialog.
 * - Escape and backdrop click both close.
 *
 * Visual design lives in the children + `className` on the panel — this only
 * adds behaviour and the fixed backdrop.
 */
const Modal = ({
  isOpen,
  onClose,
  labelledById,
  describedById,
  initialFocusRef,
  className,
  children,
}) => {
  const panelRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    previouslyFocusedRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const panel = panelRef.current;
    const frame = window.requestAnimationFrame(() => {
      const target =
        initialFocusRef?.current || getFocusable(panel)[0] || panel;
      target?.focus?.();
    });

    return () => {
      window.cancelAnimationFrame(frame);
      previouslyFocusedRef.current?.focus?.();
    };
  }, [isOpen, initialFocusRef]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose?.();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = getFocusable(panelRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        panelRef.current?.focus?.();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === panelRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledById}
        aria-describedby={describedById}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={
          className || "w-full max-w-md rounded-2xl bg-white shadow-xl"
        }
      >
        {children}
      </div>
    </div>
  );
};

export default Modal;
